'use strict';
// Canonical dependency-free protocol. frontend copy is generated by sync-gab-ui.cjs.
const VERSION = 1;
const { comparisonMarkupBlocks, normalizeComparisonMarkup } = require('./comparisonMarkup.cjs');
const { validateScene } = require('./diagramScene.cjs');
const { SCENE_DIAGRAM_EXAMPLE } = require('./diagramExamples.cjs');
// v1 shipped only on web; v2 added the complete native renderer. v3 accepts
// model-selected answers without positive keyword gates. v4 adds bounded diagrams. v5 adds contoured geometry and callouts. v6 adds solid scenes and mechanisms.
// Schema remains v1; generation catalogs are capability-gated.
// A platform name alone never enables generation on an older binary.
function supportsInteractiveAnswers(clientInfo) {
  return clientInfo?.platform === 'web' ? [1, 2, 3, 4, 5, 6].includes(clientInfo.interactiveAnswersVersion)
    : ['ios', 'android', 'macos', 'windows'].includes(clientInfo?.platform) && [2, 3, 4, 5, 6].includes(clientInfo.interactiveAnswersVersion);
}
const CATALOG = Object.freeze({
  diagram: { required: ['label','bind','parts'], props: ['label','bind','parts','explodeBind','scene','phaseBind','phaseLabel'], description: 'Selectable 2D diagram on a 1000 by 600 canvas. bind is string state: options exactly All followed by each part label. Parts: {id,label,description,shapes,offset?}; offset is [x,y] displacement at full explosion. Shapes: {type:"line",points:[[x,y],...],tone?,strokeWidth?}, {type:"circle",cx,cy,r,tone?,strokeWidth?,filled?}, or {type:"rect",x,y,width,height,tone?,strokeWidth?,filled?}. Tone is ink, muted, or accent; strokeWidth is 1–16 (omit for default); filled is boolean and allowed only on circle/rect. No URLs, SVG, HTML or code. Optional explodeBind is numeric state with min:0,max:1. The component supplies part buttons, labels, explanations and explosion control; do not duplicate these controls.' },
  tabs: { required: ['label','bind','items'], props: ['label','bind','items'], description: 'Sections shaped {label,children}; labels match the bound string state options. Only the selected section is shown.' },
  accordion: { required: ['label','children'], props: ['label','children'], description: 'Expandable detail section, initially collapsed.' },
  image: { required: ['assetId', 'alt'], props: ['assetId', 'alt', 'caption'], description: 'Photo from the supplied authorized image assets: assetId, descriptive alt, optional caption. Never invent an asset.' },
  card: { required: ['label', 'children'], props: ['label', 'children'], description: 'A titled visual card, containing image/text/controls.' },
  gallery: { required: ['children'], props: ['children'], description: 'Responsive grid of 1–12 image or card nodes.' },
  text: { required: ['text'], props: ['text'], description: 'A short explanatory paragraph.' },
  stack: { required: ['children'], props: ['children'], description: 'A vertical group of nodes.' },
  metric: { required: ['label', 'value'], props: ['label', 'value', 'format', 'currency'], description: 'A labeled calculated value; format number, currency, or percent (percentage points).' },
  numberInput: { required: ['label', 'bind'], props: ['label', 'bind'], description: 'An editable numeric input bound to numeric state.' },
  slider: { required: ['label', 'bind'], props: ['label', 'bind'], description: 'A bounded numeric control; native clients may use a stepper.' },
  select: { required: ['label', 'bind'], props: ['label', 'bind'], description: 'Choices from the bound string state options.' },
  toggle: { required: ['label', 'bind'], props: ['label', 'bind'], description: 'A boolean checkbox.' },
  checklist: { required: ['label', 'items'], props: ['label', 'items'], description: 'Items shaped {label,bind}; each binds separate boolean state.' },
  table: { required: ['label', 'headers', 'rows'], props: ['label', 'headers', 'rows'], description: 'Up to 20 rows and 6 columns, with literal or expression cells.' },
  comparison: { required: ['label', 'headers', 'rows'], props: ['label', 'headers', 'rows'], description: 'An option comparison table, same structure as table.' },
  chart: { required: ['label', 'series'], props: ['label', 'series'], description: 'A bar chart: series [{name,data:[{x:"Category",y:expression}]}], up to 6 series and 100 total points.' },
});
const LIMITS = Object.freeze({ bytes: 65536, nodes: 100, depth: 12, states: 20, operations: 2000 });
const OPS = { add: [2, 8], subtract: [2, 2], multiply: [2, 8], divide: [2, 2], min: [1, 8], max: [1, 8], round: [1, 1], ceil: [1, 1], equal: [2, 2], less: [2, 2], if: [3, 3] };
const badKeys = new Set(['__proto__', 'prototype', 'constructor']);
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const object = value => value && typeof value === 'object' && !Array.isArray(value);
const identifier = value => typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(value) && !badKeys.has(value);
// Count UTF-8 without Buffer/TextEncoder so the same bound works in Metro.
const utf8Bytes = value => { let bytes = 0; for (const char of value) { const point = char.codePointAt(0); bytes += point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4; } return bytes; };
const string = (value, max = 240) => typeof value === 'string' && value.trim().length > 0 && value.length <= max;
// The prose field precedes the large drawing payload. Recover only a complete
// top-level JSON string when later geometry is malformed or truncated; never
// repair JSON, evaluate source, or expose the raw card as the fallback.
function answerFallback(raw, spec) {
  let prose = string(spec?.fallbackMarkdown, 4000) ? spec.fallbackMarkdown : '';
  if (!prose && typeof raw === 'string' && raw.length <= LIMITS.bytes && utf8Bytes(raw) <= LIMITS.bytes) {
    let depth = 0;
    for (let i = 0; i < raw.length; i++) {
      const char = raw[i];
      if (char === '{' || char === '[') { if (++depth > 24) break; }
      else if (char === '}' || char === ']') depth--;
      else if (char === '"') {
        const start = i;
        while (++i < raw.length) {
          if (raw[i] === '\\') i++;
          else if (raw[i] === '"') break;
        }
        if (depth !== 1 || raw.slice(start, i + 1) !== '"fallbackMarkdown"') continue;
        const value = raw.slice(i + 1).match(/^\s*:\s*("(?:[^"\\]|\\[\s\S])*")/);
        try { const text = value && JSON.parse(value[1]); if (string(text, 4000)) prose = text; } catch (_) {}
        break;
      }
    }
  }
  // A caption alone can tell users to click a diagram that is absent. Retain
  // its actual part explanations when the envelope parsed but geometry failed.
  const details = [];
  let visited = 0;
  function visit(nodes, depth = 0) {
    if (!Array.isArray(nodes) || depth > LIMITS.depth) return;
    for (const node of nodes) {
      if (++visited > LIMITS.nodes) return;
      if (node?.type === 'diagram' && Array.isArray(node.parts)) {
        for (const part of node.parts.slice(0, 10)) {
          if (string(part?.label, 160) && string(part?.description, 4000)) details.push(part.label + ': ' + part.description);
        }
      }
      visit(node?.children, depth + 1);
      if (Array.isArray(node?.items)) for (const item of node.items.slice(0, 20)) visit(item?.children, depth + 1);
    }
  }
  visit(spec?.nodes);
  return [prose, ...details.filter(detail => !prose.includes(detail))].filter(Boolean).join('\n\n').slice(0, 4000)
    || 'This answer could not be completed. Please try again.';
}
function validateAnswer(raw) {
  const errors = [];
  const fail = (code, path) => { errors.push({ code, path }); throw new Error(code); };
  let spec;
  try {
    if (typeof raw !== 'string' || raw.length > LIMITS.bytes || utf8Bytes(raw) > LIMITS.bytes || !raw.trim()) fail('size', '/');
    // Bound nesting BEFORE JSON.parse and traversal, including escaped quotes.
    let depth = 0, inString = false, escape = false;
    for (const char of raw) {
      if (inString) { if (escape) escape = false; else if (char === '\\') escape = true; else if (char === '"') inString = false; }
      else if (char === '"') inString = true;
      else if (char === '{' || char === '[') { if (++depth > 24) fail('depth', '/'); }
      else if (char === '}' || char === ']') depth--;
    }
    spec = JSON.parse(raw, (key, value) => { if (badKeys.has(key)) fail('unsafe_key', '/'); return value; });
    const fields = (value, allowed, required, path) => {
      if (!object(value)) fail('object', path);
      const unknown = Object.keys(value).find(key => !allowed.includes(key));
      if (unknown !== undefined) fail('unknown_property', path + '/' + unknown);
      if (required.some(key => !own(value, key))) fail('required', path);
    };
    fields(spec, ['schemaVersion', 'answerId', 'title', 'revision', 'fallbackMarkdown', 'state', 'derived', 'nodes'], ['schemaVersion', 'answerId', 'title', 'fallbackMarkdown', 'state', 'nodes'], '/');
    if (spec.schemaVersion !== VERSION) fail('version', '/schemaVersion');
    if (!identifier(spec.answerId) || !string(spec.title, 160) || !string(spec.fallbackMarkdown, 4000)) fail('identity', '/');
    if (spec.revision !== undefined && (!Number.isInteger(spec.revision) || spec.revision < 1)) fail('revision', '/revision');
    if (!object(spec.state) || Object.keys(spec.state).length > LIMITS.states) fail('state_limit', '/state');
    for (const [key, def] of Object.entries(spec.state)) {
      if (!identifier(key)) fail('state_key', '/state');
      fields(def, ['type', 'initial', 'min', 'max', 'step', 'options'], ['type', 'initial'], '/state/' + key);
      if (def.type === 'number') {
        if (![def.initial, def.min, def.max].every(Number.isFinite) || def.min > def.max || def.initial < def.min || def.initial > def.max
          || Math.max(Math.abs(def.min), Math.abs(def.max)) > 1e12 || (def.step !== undefined && (!Number.isFinite(def.step) || def.step <= 0))) fail('number_state', '/state/' + key);
        if (def.options !== undefined) fail('unknown_property', '/state/' + key);
      } else if (def.type === 'boolean') {
        if (typeof def.initial !== 'boolean' || Object.keys(def).some(k => !['type', 'initial'].includes(k))) fail('boolean_state', '/state/' + key);
      } else if (def.type === 'string') {
        if (!Array.isArray(def.options) || def.options.length < 1 || def.options.length > 12 || !def.options.every(v => string(v, 80))
          || new Set(def.options).size !== def.options.length || !def.options.includes(def.initial)
          || Object.keys(def).some(k => !['type', 'initial', 'options'].includes(k))) fail('choice_state', '/state/' + key);
      } else fail('state_type', '/state/' + key);
    }
    if (spec.derived === undefined) spec.derived = {};
    if (!object(spec.derived) || Object.keys(spec.derived).length > 40) fail('derived_limit', '/derived');
    let expressions = 0;
    function expr(value, path, nesting = 0) {
      if (++expressions > LIMITS.operations || nesting > LIMITS.depth) fail('expression_limit', path);
      if (typeof value === 'number') { if (!Number.isFinite(value) || Math.abs(value) > 1e15) fail('number', path); return; }
      if (typeof value === 'boolean' || (typeof value === 'string' && value.length <= 240)) return;
      if (!object(value)) fail('expression', path);
      if (own(value, 'state')) {
        fields(value, ['state'], ['state'], path);
        if (!identifier(value.state) || !own(spec.state, value.state)) fail('state_reference', path);
      } else if (own(value, 'derived')) {
        fields(value, ['derived'], ['derived'], path);
        if (!identifier(value.derived) || !own(spec.derived, value.derived)) fail('derived_reference', path);
      } else {
        fields(value, ['op', 'args'], ['op', 'args'], path);
        const range = own(OPS, value.op) && OPS[value.op];
        if (!range || !Array.isArray(value.args) || value.args.length < range[0] || value.args.length > range[1]) fail('operation', path);
        value.args.forEach((v, i) => expr(v, path + '/args/' + i, nesting + 1));
      }
    }
    for (const [key, value] of Object.entries(spec.derived)) { if (!identifier(key)) fail('derived_key', '/derived'); expr(value, '/derived/' + key); }
    // Validate all dependency edges, including currently unselected conditional branches.
    const visited = new Set(), visiting = new Set();
    function dependency(value) {
      if (!object(value)) return;
      if (own(value, 'derived')) visit(value.derived);
      if (Array.isArray(value.args)) value.args.forEach(dependency);
    }
    function visit(key) { if (visiting.has(key)) fail('cycle', '/derived/' + key); if (visited.has(key)) return;
      visiting.add(key); dependency(spec.derived[key]); visiting.delete(key); visited.add(key); }
    Object.keys(spec.derived).forEach(visit);
    let nodes = 0, points = 0, diagramShapes = 0, diagramPoints = 0, sceneSolids = 0, sceneFaces = 0;
    const ids = new Set();
    function node(value, path, nesting = 0) {
      if (++nodes > LIMITS.nodes || nesting > LIMITS.depth) fail('node_limit', path);
      if (!object(value) || !identifier(value.id) || ids.has(value.id) || !own(CATALOG, value.type)) fail('node', path);
      ids.add(value.id);
      const definition = CATALOG[value.type];
      fields(value, ['id', 'type', ...definition.props], ['id', 'type', ...definition.required], path);
      if (value.label !== undefined && !string(value.label, 160)) fail('label', path);
      function binding(key, type) { if (!identifier(key) || !own(spec.state, key) || spec.state[key].type !== type) fail('binding', path); }
      if (['numberInput', 'slider'].includes(value.type)) binding(value.bind, 'number');
      if (value.type === 'select') binding(value.bind, 'string');
      if (value.type === 'diagram') {
        binding(value.bind, 'string');
        if (value.explodeBind !== undefined) {
          binding(value.explodeBind, 'number');
          if (spec.state[value.explodeBind].min !== 0 || spec.state[value.explodeBind].max !== 1) fail('diagram_explosion', path);
        }
        if (value.scene !== undefined) {
          const complexity=validateScene(value,spec.state,fail,path);
          sceneSolids+=complexity.solids;sceneFaces+=complexity.faces;
          if(sceneSolids>64||sceneFaces>1800)fail('diagram_scene_limit',path);
        } else {
        if (value.phaseBind !== undefined || value.phaseLabel !== undefined) fail('diagram_phase',path);
        if (!Array.isArray(value.parts) || value.parts.length < 2 || value.parts.length > 10) fail('diagram_parts', path);
        const partIds = new Set(), partLabels = new Set();
        for (const [partIndex, part] of value.parts.entries()) {
          const partPath = path + '/parts/' + partIndex;
          fields(part, ['id','label','description','shapes','offset','callout'], ['id','label','description','shapes'], partPath);
          if (!identifier(part.id) || partIds.has(part.id) || !string(part.label,80) || part.label === 'All' || partLabels.has(part.label) || !string(part.description,1200)) fail('diagram_part', partPath);
          partIds.add(part.id); partLabels.add(part.label);
          const offset = part.offset || [0,0];
          if (!Array.isArray(offset) || offset.length !== 2 || !offset.every(n => Number.isFinite(n) && Math.abs(n) <= 300)) fail('diagram_offset',partPath);
          if (part.callout !== undefined) {
            fields(part.callout, ['anchor','position'], ['anchor','position'], partPath+'/callout');
            for (const key of ['anchor','position']) {
              const point = part.callout[key];
              if (!Array.isArray(point) || point.length !== 2 || !point.every(Number.isFinite)) fail('diagram_callout',partPath+'/callout/'+key);
              const [x,y] = point;
              if (x < 0 || y < 0 || x > (key === 'position' ? 820 : 1000) || y > (key === 'position' ? 560 : 600)) fail('diagram_callout',partPath+'/callout/'+key);
            }
            const [x,y] = part.callout.anchor;
            if (value.explodeBind && (x+offset[0]<0 || x+offset[0]>1000 || y+offset[1]<0 || y+offset[1]>600)) fail('diagram_callout',partPath+'/callout/anchor');
          }
          if (!Array.isArray(part.shapes) || !part.shapes.length || part.shapes.length > 40) fail('diagram_shapes',partPath);
          for (const [shapeIndex, shape] of part.shapes.entries()) {
            const shapePath = partPath + '/shapes/' + shapeIndex;
            if (++diagramShapes > 200 || !object(shape)) fail('diagram_shapes',shapePath);
            const common = ['type','tone','strokeWidth','filled'];
            const geometry = ['line','polygon','curve'].includes(shape.type) ? ['points'] : shape.type === 'circle' ? ['cx','cy','r'] : shape.type === 'rect' ? ['x','y','width','height'] : shape.type === 'ellipse' ? ['cx','cy','rx','ry'] : null;
            if (!geometry) fail('diagram_shape',shapePath);
            fields(shape,[...common,...geometry],['type',...geometry],shapePath);
            if ((shape.tone !== undefined && !['ink','muted','accent','blue','teal','amber','rose'].includes(shape.tone)) || (shape.filled !== undefined && (typeof shape.filled !== 'boolean' || ['line','curve'].includes(shape.type))) || (shape.strokeWidth !== undefined && (!Number.isFinite(shape.strokeWidth) || shape.strokeWidth < 1 || shape.strokeWidth > 16))) fail('diagram_style',shapePath);
            let bounds;
            if (['line','polygon','curve'].includes(shape.type)) {
              if (!Array.isArray(shape.points) || shape.points.length < 2 || shape.points.length > 32) fail('diagram_points',shapePath);
              if ((shape.type === 'polygon' && shape.points.length < 3) || (shape.type === 'curve' && shape.points.length !== 4)) fail('diagram_points',shapePath);
              for (const p of shape.points) if (++diagramPoints > 800 || !Array.isArray(p) || p.length !== 2 || !p.every(Number.isFinite)) fail('diagram_points',shapePath);
              bounds = [Math.min(...shape.points.map(p=>p[0])),Math.min(...shape.points.map(p=>p[1])),Math.max(...shape.points.map(p=>p[0])),Math.max(...shape.points.map(p=>p[1]))];
            } else if (shape.type === 'ellipse') {
              if (![shape.cx,shape.cy,shape.rx,shape.ry].every(Number.isFinite) || shape.rx <= 0 || shape.ry <= 0 || shape.rx > 500 || shape.ry > 300) fail('diagram_geometry',shapePath);
              bounds = [shape.cx-shape.rx,shape.cy-shape.ry,shape.cx+shape.rx,shape.cy+shape.ry];
            } else if (shape.type === 'circle') {
              if (![shape.cx,shape.cy,shape.r].every(Number.isFinite) || shape.r <= 0 || shape.r > 300) fail('diagram_geometry',shapePath);
              bounds = [shape.cx-shape.r,shape.cy-shape.r,shape.cx+shape.r,shape.cy+shape.r];
            } else {
              if (![shape.x,shape.y,shape.width,shape.height].every(Number.isFinite) || shape.width <= 0 || shape.height <= 0) fail('diagram_geometry',shapePath);
              bounds = [shape.x,shape.y,shape.x+shape.width,shape.y+shape.height];
            }
            for (const fraction of [0, value.explodeBind ? 1 : 0]) {
              if (bounds[0]+offset[0]*fraction < 0 || bounds[1]+offset[1]*fraction < 0 || bounds[2]+offset[0]*fraction > 1000 || bounds[3]+offset[1]*fraction > 600) fail('diagram_bounds',shapePath);
            }
          }
        }
        const options = spec.state[value.bind].options;
        if (options.length !== value.parts.length+1 || options[0] !== 'All' || value.parts.some((part,i)=>options[i+1] !== part.label)) fail('diagram_choices',path);
        if (value.explodeBind && !value.parts.some(part=>part.offset?.some(n=>n!==0))) fail('diagram_explosion',path);
        }
      }
      if (value.type === 'toggle') binding(value.bind, 'boolean');
      if (value.type === 'text' && !string(value.text, 2000)) fail('text', path);
      if (value.type === 'metric') {
        expr(value.value, path + '/value');
        if (value.format !== undefined && !['number', 'currency', 'percent'].includes(value.format)) fail('format', path);
        if (value.currency !== undefined && !/^[A-Z]{3}$/.test(value.currency)) fail('currency', path);
      }
      if (['stack','card','gallery','accordion'].includes(value.type)) {
        if (!Array.isArray(value.children) || !value.children.length) fail('children', path);
        if (value.type === 'gallery' && (value.children.length > 12 || value.children.some(child => !['image','card'].includes(child?.type)))) fail('gallery', path);
        value.children.forEach((v, i) => node(v, path + '/children/' + i, nesting + 1));
      }
      if (value.type === 'tabs') {
        binding(value.bind, 'string');
        if (!Array.isArray(value.items) || value.items.length !== spec.state[value.bind].options.length || new Set(value.items.map(item => item.label)).size !== value.items.length) fail('tabs',path);
        value.items.forEach((item,index) => { fields(item,['label','children'],['label','children'],path); if (!spec.state[value.bind].options.includes(item.label) || !Array.isArray(item.children) || !item.children.length) fail('tabs',path); item.children.forEach((child,i)=>node(child,path+'/items/'+index+'/children/'+i,nesting+1)); });
      }
      if (value.type === 'image' && (!/^[a-fA-F0-9]{24}$/.test(value.assetId) || !string(value.alt, 240) || (value.caption !== undefined && !string(value.caption, 400)))) fail('image_asset', path);
      if (value.type === 'checklist') {
        if (!Array.isArray(value.items) || value.items.length < 1 || value.items.length > 20) fail('items', path);
        value.items.forEach(item => { fields(item, ['label', 'bind'], ['label', 'bind'], path); if (!string(item.label, 160)) fail('label', path); binding(item.bind, 'boolean'); });
      }
      if (['table', 'comparison'].includes(value.type)) {
        if (!Array.isArray(value.headers) || value.headers.length < 1 || value.headers.length > 6 || !value.headers.every(v => string(v, 80))
          || !Array.isArray(value.rows) || value.rows.length < 1 || value.rows.length > 20) fail('table', path);
        value.rows.forEach(row => { if (!Array.isArray(row) || row.length !== value.headers.length) fail('row', path); row.forEach(v => expr(v, path)); });
      }
      if (value.type === 'chart') {
        if (!Array.isArray(value.series) || !value.series.length || value.series.length > 6) fail('series', path);
        value.series.forEach(series => {
          fields(series, ['name', 'data'], ['name', 'data'], path);
          if (!string(series.name, 60) || !Array.isArray(series.data) || !series.data.length) fail('series', path);
          series.data.forEach(point => { fields(point, ['x', 'y'], ['x', 'y'], path); if (++points > 100 || !string(point.x, 48)) fail('point', path); expr(point.y, path); });
        });
      }
    }
    if (!Array.isArray(spec.nodes) || !spec.nodes.length) fail('nodes', '/nodes');
    spec.nodes.forEach((v, i) => node(v, '/nodes/' + i));
    const initial = evaluateAnswer(spec);
    if (initial.errors.length) fail('initial_evaluation', initial.errors[0].path);
    return { spec, errors: [], fallback: answerFallback(raw, spec) };
  } catch (_) {
    return { spec: null, errors: errors.length ? errors : [{ code: 'json', path: '/' }], fallback: answerFallback(raw, spec) };
  }
}
// Read only completed JSON values. Never invent a quote, scalar or leaf node to
// make an unfinished payload valid. The only open structures we expose are the
// envelope, node arrays and layout containers whose complete children validate.
function validateStreamingAnswer(raw) {
  const full = validateAnswer(raw);
  if (full.spec) return { ...full, pending: false };
  const none = { ...full, pending: true };
  if (typeof raw !== 'string' || raw.length > LIMITS.bytes || utf8Bytes(raw) > LIMITS.bytes) return none;
  let cursor = 0;
  const space = () => { while (/\s/.test(raw[cursor] || '') && cursor < raw.length) cursor++; };
  function read(depth = 0) {
    if (depth > 24) throw new Error('depth');
    space();
    if (cursor === raw.length) return { complete: false };
    const start = cursor, token = raw[cursor++];
    if (token === '"') {
      let escaped = false;
      while (cursor < raw.length) {
        const char = raw[cursor++];
        if (escaped) escaped = false;
        else if (char === '\\') escaped = true;
        else if (char === '"') return { complete: true, value: JSON.parse(raw.slice(start, cursor)) };
      }
      return { complete: false };
    }
    if (token === '{' || token === '[') {
      const isObject = token === '{', close = isObject ? '}' : ']';
      const value = isObject ? Object.create(null) : [];
      space();
      if (raw[cursor] === close) { cursor++; return { complete: true, value }; }
      while (cursor < raw.length) {
        let key;
        if (isObject) {
          if (raw[cursor] !== '"') throw new Error('key');
          const field = read(depth + 1);
          if (!field.complete) return { complete: false, value };
          key = field.value;
          if (badKeys.has(key) || own(value, key)) throw new Error('unsafe_key');
          space();
          if (cursor === raw.length) return { complete: false, value };
          if (raw[cursor++] !== ':') throw new Error('colon');
        }
        const child = read(depth + 1);
        if (!child.complete) return { complete: false, value, key, child };
        if (isObject) value[key] = child.value; else value.push(child.value);
        space();
        if (cursor === raw.length) return { complete: false, value };
        if (raw[cursor] === close) { cursor++; return { complete: true, value }; }
        if (raw[cursor++] !== ',') throw new Error('separator');
        space();
        if (raw[cursor] === close) throw new Error('trailing_comma');
      }
      return { complete: false, value };
    }
    while (cursor < raw.length && !/[\s,}\]]/.test(raw[cursor])) cursor++;
    if (cursor === raw.length) return { complete: false };
    return { complete: true, value: JSON.parse(raw.slice(start, cursor)) };
  }
  function nodes(array) {
    if (!Array.isArray(array?.value)) return [];
    const ready = array.value.slice();
    const child = array.child;
    if (child && ['stack', 'card', 'gallery', 'accordion'].includes(child.value?.type)
      && child.key === 'children' && child.child) {
      const children = nodes(child.child);
      if (children.length) ready.push({ ...child.value, children });
    }
    return ready;
  }
  try {
    const root = read();
    // A finished invalid document must never masquerade as a partial success.
    if (root.complete || !object(root.value)) return none;
    const candidate = { ...root.value };
    if (root.key === 'nodes') candidate.nodes = nodes(root.child);
    const validated = validateAnswer(JSON.stringify(candidate));
    // Keep incomplete geometry out of the renderable spec, but let the card
    // reserve its drawing area while that node's payload is still arriving.
    let pending = root.child, pendingKind;
    while (pending) {
      if (pending.value?.type === 'diagram') { pendingKind = 'diagram'; break; }
      pending = pending.child;
    }
    return validated.spec ? { ...validated, pending: true, pendingKind } : { ...none, pendingKind };
  } catch (_) { return none; }
}

// Generation quality check: run before storing new answers, without invalidating
// older persisted protocol documents or preference forms meant for chat follow-up.
function interactionQualityErrors(spec, ask = '') {
  const used = new Set(), controls = [], labels = [], tables = [], currencyMetrics = [], checklistLabels = new Map(), tabBindings = new Set(), errors = [];
  function expression(value) {
    if (!object(value)) return;
    if (value.state) used.add(value.state);
    if (value.derived && !used.has('derived:' + value.derived)) {
      used.add('derived:' + value.derived); expression(spec.derived[value.derived]);
    }
    if (Array.isArray(value.args)) value.args.forEach(expression);
  }
  function nodes(items) { for (const n of items) {
    if (['numberInput','slider','select','toggle'].includes(n.type)) controls.push(n);
    else if (n.label) labels.push({label:n.label,path:'/nodes/'+n.id});
    if (n.type === 'tabs') { tabBindings.add(n.bind); used.add(n.bind); n.items.forEach(i => nodes(i.children)); }
    if (n.type === 'diagram') { tabBindings.add(n.bind); used.add(n.bind); if(n.explodeBind) { tabBindings.add(n.explodeBind); used.add(n.explodeBind); } if(n.phaseBind) { tabBindings.add(n.phaseBind); used.add(n.phaseBind); } }
    if (n.type === 'metric') { expression(n.value); if (n.format === 'currency' && object(n.value) && !n.value.state) currencyMetrics.push(n.id); }
    if (['table','comparison'].includes(n.type)) { tables.push(n); n.rows.forEach(r => r.forEach(expression)); }
    if (n.type === 'chart') n.series.forEach(s => s.data.forEach(p => expression(p.y)));
    if (n.type === 'checklist') n.items.forEach(i => {
      used.add(i.bind); labels.push({label:i.label,path:'/state/'+i.bind});
      if (checklistLabels.has(i.bind) && checklistLabels.get(i.bind) !== i.label)
        errors.push({code:'shared_checklist_state',path:'/state/'+i.bind});
      checklistLabels.set(i.bind, i.label);
    });
    if (n.children) nodes(n.children);
  } }
  nodes(spec.nodes);
  for (const [key, value] of Object.entries(spec.derived || {})) {
    if (value?.op === 'if' && value.args?.[0]?.op === 'less' && typeof value.args[0].args?.[1] === 'number' && value.args[0].args[1] > 0
      && value.args.slice(1).some(branch => typeof branch === 'string' && /unavailable[\s\S]*margin[\s\S]*positive/i.test(branch)))
      errors.push({code:'positive_margin_cutoff',path:'/derived/'+key});
  }
  const seatCapacity = String(ask).match(/\bfor\s+(\d+)\s+seats\b/i);
  if (seatCapacity && /\bticket\b/i.test(ask)) {
    for (const n of controls) if (/\b(?:tickets?|seats?) (?:sold|sales)|\battendees?\b/i.test(n.label) && spec.state[n.bind]?.type === 'number' && spec.state[n.bind].max > Number(seatCapacity[1]))
      errors.push({code:'exceeds_stated_seat_capacity',path:'/state/'+n.bind+'/max'});
  }
  for (const n of controls) if (tabBindings.has(n.bind)) errors.push({code:'duplicate_navigation',path:'/nodes/'+n.id});
  if (!/\b(?:preferences? form|intake form|questionnaire)\b/i.test(ask))
    for (const n of controls) if (!used.has(n.bind)) errors.push({code:'disconnected_control',path:'/nodes/'+n.id});
  // A time target can be editable, but a fixed 'Monday (20 min)' label then
  // contradicts it. Titles/labels cannot contain expressions in this protocol.
  for (const c of controls.filter(n => ['numberInput','slider'].includes(n.type))) {
    const units = c.label.match(/\b(?:minutes?|mins?|hours?|hrs?)\b/i);
    if (!units) continue;
    const unitPattern = /^(?:m)/i.test(units[0]) ? '(?:minutes?|mins?)' : '(?:hours?|hrs?)';
    const fixed = new RegExp('(?:^|[^0-9.])' + spec.state[c.bind].initial + '\\s*' + unitPattern + '\\b','i');
    for (const l of labels) if (fixed.test(l.label)) errors.push({code:'fixed_adjustable_label',path:l.path});
  }
  function references(value, result = new Set(), derivedSeen = new Set()) {
    if (!object(value)) return result;
    if (value.state) result.add(value.state);
    if (value.derived && !derivedSeen.has(value.derived)) { derivedSeen.add(value.derived); references(spec.derived[value.derived], result, derivedSeen); }
    if (value.args) value.args.forEach(v => references(v, result, derivedSeen));
    return result;
  }
  for (const t of tables) t.rows.forEach((row,index) => {
    const refs = new Set(); row.forEach(v => references(v, refs));
    const money = row.filter(v => typeof v === 'string').flatMap(v => Array.from(v.matchAll(/\$([0-9][0-9,]*(?:\.[0-9]+)?)/g), m => Number(m[1].replace(/,/g,''))));
    if (controls.some(c => spec.state[c.bind].type === 'number' && refs.has(c.bind) && money.includes(spec.state[c.bind].initial)))
      errors.push({code:'fixed_adjustable_row_label',path:'/nodes/'+t.id+'/rows/'+index});
  });
  if (/\b(?:saving|savings)\b/i.test(ask) && controls.some(c => spec.state[c.bind].type === 'number')) {
    for (const t of tables) t.rows.forEach((row,index) => {
      if (row.some(v => typeof v === 'string' && /\b(?:current|your) (?:plan|rate|deposit|contribution)\b/i.test(v) && /\$[0-9]/.test(v)))
        errors.push({code:'fixed_current_savings_label',path:'/nodes/'+t.id+'/rows/'+index});
    });
    for (const t of tables) if (/scenario|timeline|benchmark|rate/i.test(t.label) &&
      t.rows.some(row => !row.some(object) || row.slice(1).some(v => typeof v === 'string' && /\b[0-9]+(?:\.[0-9]+)?\s*(?:months?|years?|weeks?)\b/i.test(v))))
      errors.push({code:'fixed_savings_scenario',path:'/nodes/'+t.id});
  }
  if (currencyMetrics.length) {
    const amounts = new Set();
    function collect(items) { for (const n of items) {
      if (currencyMetrics.includes(n.id) && Number.isFinite(n.value)) amounts.add(n.value);
      if (n.children) collect(n.children);
      if (n.type === 'tabs') n.items.forEach(i => collect(i.children));
    } }
    collect(evaluateAnswer(spec).nodes);
    for (const t of tables) {
      const literals = [t.label,...t.headers,...t.rows.flat()].filter(v => typeof v === 'string').join(' ');
      const money = Array.from(literals.matchAll(/\$([0-9][0-9,]*(?:\.[0-9]+)?)/g), m => Number(m[1].replace(/,/g,'')));
      if (money.some(v => amounts.has(v))) errors.push({code:'fixed_adjustable_table',path:'/nodes/'+t.id});
    }
  }
  return errors;
}
function validStateValue(def, value) {
  return def.type === 'number' ? Number.isFinite(value) && value >= def.min && value <= def.max
    : def.type === 'boolean' ? typeof value === 'boolean' : def.options.includes(value);
}
function hydrateState(spec, saved = {}) {
  return Object.fromEntries(Object.entries(spec.state).map(([key, def]) => [key, own(saved, key) && validStateValue(def, saved[key]) ? saved[key] : def.initial]));
}
function evaluateAnswer(spec, saved = {}) {
  const state = hydrateState(spec, saved), derived = Object.create(null), errors = [];
  let operations = 0;
  const active = new Set();
  function evaluate(value, depth = 0) {
    if (++operations > LIMITS.operations || depth > 24) throw Error('expression_limit');
    if (!object(value)) return value;
    if (own(value, 'state')) return state[value.state];
    if (own(value, 'derived')) return resolve(value.derived, depth + 1);
    const op = value.op;
    if (op === 'if') return evaluate(value.args[evaluate(value.args[0], depth + 1) ? 1 : 2], depth + 1);
    const args = value.args.map(v => evaluate(v, depth + 1));
    if (op === 'equal') return args[0] === args[1];
    if (!args.every(v => typeof v === 'number' && Number.isFinite(v))) throw Error('numeric_operands');
    let result;
    switch (op) {
      case 'add': result = args.reduce((a, b) => a + b, 0); break;
      case 'subtract': result = args[0] - args[1]; break;
      case 'multiply': result = args.reduce((a, b) => a * b, 1); break;
      case 'divide': if (args[1] === 0) throw Error('division_by_zero'); result = args[0] / args[1]; break;
      case 'min': result = Math.min(...args); break;
      case 'max': result = Math.max(...args); break;
      case 'round': result = Math.round(args[0]); break;
      case 'ceil': result = Math.ceil(args[0]); break;
      case 'less': return args[0] < args[1];
      default: throw Error('operation');
    }
    if (!Number.isFinite(result) || Math.abs(result) > 1e15) throw Error('non_finite');
    return result;
  }
  function resolve(key, depth) {
    if (own(derived, key)) return derived[key];
    if (active.has(key)) throw Error('cycle');
    active.add(key); const value = evaluate(spec.derived[key], depth); active.delete(key); derived[key] = value; return value;
  }
  function node(value) {
    try {
      if (['stack','card','gallery','accordion'].includes(value.type)) return { ...value, children: value.children.map(node) };
      if (value.type === 'tabs') return {...value,items:value.items.map(item=>({...item,children:item.children.map(node)}))};
      if (value.type === 'metric') return { ...value, value: evaluate(value.value) };
      if (['table', 'comparison'].includes(value.type)) return { ...value, rows: value.rows.map(row => row.map(v => evaluate(v))) };
      if (value.type === 'chart') return { ...value, series: value.series.map(s => ({ ...s, data: s.data.map(p => {
        const y = evaluate(p.y); if (!Number.isFinite(y)) throw Error('numeric_chart'); return { ...p, y };
      }) })) };
      return value;
    } catch (error) { errors.push({ code: error.message, path: '/nodes/' + value.id }); return { ...value, failed: true }; }
  }
  return { state, nodes: spec.nodes.map(node), errors };
}
const EXAMPLE = {
  schemaVersion: VERSION, answerId: 'dinner-budget', title: 'Community dinner budget', revision: 1,
  fallbackMarkdown: 'Illustrative budget: 80 guests at $10 per meal plus $200 fixed costs uses $1,000 of a $1,200 budget, leaving $200. Adjust the inputs to explore alternatives.',
  state: { guests: { type: 'number', initial: 80, min: 1, max: 500, step: 1 }, mealCost: { type: 'number', initial: 10, min: 0, max: 100, step: 0.5 }, budget: { type: 'number', initial: 1200, min: 0, max: 100000 }, fixedCost: { type: 'number', initial: 200, min: 0, max: 100000 }, venue: { type: 'boolean', initial: false } },
  derived: { food: { op: 'multiply', args: [{ state: 'guests' }, { state: 'mealCost' }] }, total: { op: 'add', args: [{ derived: 'food' }, { state: 'fixedCost' }] }, remaining: { op: 'subtract', args: [{ state: 'budget' }, { derived: 'total' }] } },
  nodes: [
    { id: 'assumptions', type: 'text', text: 'Meal and fixed costs are illustrative assumptions, not vendor quotes.' },
    { id: 'guests', type: 'slider', label: 'Guests', bind: 'guests' }, { id: 'meal', type: 'numberInput', label: 'Cost per meal', bind: 'mealCost' },
    { id: 'budget', type: 'numberInput', label: 'Budget', bind: 'budget' }, { id: 'fixed', type: 'numberInput', label: 'Fixed costs', bind: 'fixedCost' },
    { id: 'total', type: 'metric', label: 'Total cost', value: { derived: 'total' }, format: 'currency', currency: 'USD' },
    { id: 'remaining', type: 'metric', label: 'Remaining budget', value: { derived: 'remaining' }, format: 'currency', currency: 'USD' },
    { id: 'breakdown', type: 'chart', label: 'Cost breakdown', series: [{ name: 'Cost', data: [{ x: 'Meals', y: { derived: 'food' } }, { x: 'Fixed costs', y: { state: 'fixedCost' } }] }] },
    { id: 'preparation', type: 'checklist', label: 'Preparation', items: [{ label: 'Confirm the venue', bind: 'venue' }] },
  ],
};
const NON_NUMERIC_EXAMPLES = [
  { schemaVersion:1, answerId:'trip-preparation', title:'Trip preparation', fallbackMarkdown:'Pack your ID and charger. Both tasks are initially incomplete.',
    state:{ idPacked:{type:'boolean',initial:false}, chargerPacked:{type:'boolean',initial:false} },
    nodes:[{id:'packing',type:'checklist',label:'Packing progress',items:[{label:'Pack ID',bind:'idPacked'},{label:'Pack charger',bind:'chargerPacked'}]}] },
  { schemaVersion:1, answerId:'decision-guide', title:'Choose a workflow', fallbackMarkdown:'Choose speed for a quick draft, or detail for a thorough review.',
    state:{priority:{type:'string',initial:'Speed',options:['Speed','Detail']}},
    nodes:[{id:'priority',type:'select',label:'Your priority',bind:'priority'}, {id:'next',type:'metric',label:'Suggested next step',value:{op:'if',args:[{op:'equal',args:[{state:'priority'},'Speed']},'Start with a quick draft.','Start with a thorough review.']}}] },
];
const DIAGRAM_EXAMPLE = {
  "schemaVersion": 1,
  "answerId": "bicycle-systems",
  "title": "Explore a 7-speed bicycle",
  "fallbackMarkdown": "A common 1×7 bicycle combines a frame, two wheels, a seven-sprocket drivetrain, brakes and cockpit. Select a system for its role. This simplified diagram is illustrative, not a repair manual.",
  "state": {
    "part": {
      "type": "string",
      "initial": "All",
      "options": [
        "All",
        "Frame",
        "Wheels",
        "Drivetrain",
        "Brakes",
        "Cockpit"
      ]
    },
    "separation": {
      "type": "number",
      "initial": 0,
      "min": 0,
      "max": 1,
      "step": 0.1
    }
  },
  "nodes": [
    {
      "id": "bike",
      "type": "diagram",
      "label": "Bicycle systems",
      "bind": "part",
      "explodeBind": "separation",
      "parts": [
        {
          "id": "frame",
          "label": "Frame",
          "description": "The frame links the wheels, saddle and steering assembly. Triangles carry loads efficiently. This is a simplified side view, not an assembly drawing.",
          "offset": [
            0,
            -65
          ],
          "shapes": [
            {
              "type": "line",
              "points": [
                [
                  230,
                  410
                ],
                [
                  400,
                  220
                ],
                [
                  470,
                  420
                ],
                [
                  230,
                  410
                ],
                [
                  660,
                  200
                ],
                [
                  470,
                  420
                ],
                [
                  400,
                  220
                ],
                [
                  660,
                  200
                ]
              ],
              "strokeWidth": 12
            }
          ]
        },
        {
          "id": "wheels",
          "label": "Wheels",
          "description": "Two spoked wheels support the bicycle and roll on tires. The rear hub receives power through the chain.",
          "offset": [
            0,
            55
          ],
          "shapes": [
            {
              "type": "circle",
              "cx": 230,
              "cy": 410,
              "r": 110,
              "strokeWidth": 9
            },
            {
              "type": "circle",
              "cx": 770,
              "cy": 410,
              "r": 110,
              "strokeWidth": 9
            },
            {
              "type": "circle",
              "cx": 230,
              "cy": 410,
              "r": 96,
              "tone": "muted"
            },
            {
              "type": "circle",
              "cx": 770,
              "cy": 410,
              "r": 96,
              "tone": "muted"
            },
            {
              "type": "line",
              "points": [
                [
                  134,
                  410
                ],
                [
                  326,
                  410
                ]
              ],
              "tone": "muted"
            },
            {
              "type": "line",
              "points": [
                [
                  674,
                  410
                ],
                [
                  866,
                  410
                ]
              ],
              "tone": "muted"
            },
            {
              "type": "line",
              "points": [
                [
                  230,
                  314
                ],
                [
                  230,
                  506
                ]
              ],
              "tone": "muted"
            },
            {
              "type": "line",
              "points": [
                [
                  770,
                  314
                ],
                [
                  770,
                  506
                ]
              ],
              "tone": "muted"
            }
          ]
        },
        {
          "id": "drive",
          "label": "Drivetrain",
          "description": "Pedals turn a front chainring. The chain drives one of seven rear sprockets, selected by a rear derailleur. Seven speeds here means a common 1×7 setup; exact parts vary by bicycle.",
          "offset": [
            0,
            0
          ],
          "shapes": [
            {
              "type": "circle",
              "cx": 470,
              "cy": 420,
              "r": 35,
              "strokeWidth": 6
            },
            {
              "type": "circle",
              "cx": 230,
              "cy": 410,
              "r": 20,
              "strokeWidth": 5
            },
            {
              "type": "line",
              "points": [
                [
                  470,
                  385
                ],
                [
                  230,
                  390
                ],
                [
                  215,
                  423
                ],
                [
                  470,
                  455
                ]
              ],
              "strokeWidth": 4
            },
            {
              "type": "line",
              "points": [
                [
                  470,
                  420
                ],
                [
                  504,
                  465
                ],
                [
                  529,
                  465
                ]
              ],
              "strokeWidth": 7
            },
            {
              "type": "line",
              "points": [
                [
                  230,
                  423
                ],
                [
                  242,
                  448
                ],
                [
                  226,
                  469
                ]
              ],
              "strokeWidth": 7
            }
          ]
        },
        {
          "id": "brakes",
          "label": "Brakes",
          "description": "Brake levers operate front and rear brakes. This illustration uses simplified rim-brake positions; a particular bicycle may use disc brakes instead.",
          "offset": [
            40,
            -20
          ],
          "shapes": [
            {
              "type": "line",
              "points": [
                [
                  738,
                  296
                ],
                [
                  752,
                  282
                ],
                [
                  775,
                  288
                ]
              ],
              "strokeWidth": 7
            },
            {
              "type": "line",
              "points": [
                [
                  282,
                  293
                ],
                [
                  300,
                  287
                ],
                [
                  317,
                  304
                ]
              ],
              "strokeWidth": 7
            }
          ]
        },
        {
          "id": "cockpit",
          "label": "Cockpit",
          "description": "The handlebar and stem steer the fork and front wheel. The saddle and seatpost support the rider and set the sitting position.",
          "offset": [
            0,
            -75
          ],
          "shapes": [
            {
              "type": "line",
              "points": [
                [
                  770,
                  410
                ],
                [
                  665,
                  175
                ],
                [
                  685,
                  105
                ],
                [
                  745,
                  105
                ]
              ],
              "strokeWidth": 9
            },
            {
              "type": "line",
              "points": [
                [
                  385,
                  210
                ],
                [
                  365,
                  135
                ]
              ],
              "strokeWidth": 9
            },
            {
              "type": "line",
              "points": [
                [
                  320,
                  130
                ],
                [
                  395,
                  130
                ]
              ],
              "strokeWidth": 14
            }
          ]
        }
      ]
    }
  ]
};
// Rich geometry example is emitted only to clients with the v5 renderer.
const RICH_DIAGRAM_EXAMPLE = {
  "schemaVersion": 1,
  "answerId": "helicopter-systems",
  "title": "Explore a helicopter",
  "fallbackMarkdown": "A helicopter combines an airframe, cockpit, powertrain, landing gear, sensors and rotors. The engine drives the main rotor to produce lift; the tail rotor counters torque. This general side view is illustrative, and equipment varies by aircraft.",
  "state": {"part":{"type":"string","initial":"All","options":["All","Airframe","Cockpit","Powertrain","Landing gear","Sensors","Rotors"]},"separation":{"type":"number","initial":0,"min":0,"max":1,"step":0.1}},
  "nodes": [{
    "id": "helicopter",
    "type": "diagram",
    "label": "Helicopter systems",
    "bind": "part",
    "explodeBind": "separation",
    "parts": [
      {
        "id": "airframe",
        "label": "Airframe",
        "description": "The fuselage carries the crew and equipment. The tapered tail boom supports the tail rotor. This is a general illustrative side view; configurations vary.",
        "offset": [0,20],
        "callout": {"anchor":[784,291],"position":[775,190]},
        "shapes": [
          {"type":"polygon","points":[[145,333],[168,301],[215,276],[289,251],[356,255],[390,279],[469,292],[530,307],[752,283],[796,225],[833,225],[838,312],[769,326],[543,339],[493,372],[240,378],[179,364]],"tone":"ink","filled":true,"strokeWidth":2},
          {"type":"curve","points":[[193,354],[283,364],[421,359],[500,338]],"tone":"muted","strokeWidth":2},
          {"type":"line","points":[[525,320],[778,302]],"tone":"muted","strokeWidth":1},
          {"type":"line","points":[[349,278],[351,355]],"tone":"muted","strokeWidth":1},
          {"type":"line","points":[[407,300],[405,357]],"tone":"muted","strokeWidth":1},
          {"type":"line","points":[[284,335],[336,335]],"tone":"muted","strokeWidth":1},
          {"type":"line","points":[[799,249],[817,247]],"tone":"muted","strokeWidth":1},
          {"type":"line","points":[[755,318],[806,318]],"tone":"muted","strokeWidth":1},
          {"type":"polygon","points":[[430,332],[470,318],[501,326],[459,345]],"tone":"muted","filled":true,"strokeWidth":2},
          {"type":"line","points":[[447,338],[480,325]],"tone":"ink","strokeWidth":1}
        ]
      },
      {
        "id": "cockpit",
        "label": "Cockpit",
        "description": "The canopy provides visibility. The pilot changes rotor pitch with the flight controls to adjust lift and the direction of travel.",
        "offset": [-25,-20],
        "callout": {"anchor":[259,290],"position":[45,205]},
        "shapes": [
          {"type":"polygon","points":[[178,303],[218,280],[276,261],[285,306],[252,326],[182,326]],"tone":"blue","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[292,264],[341,267],[369,291],[370,322],[296,315]],"tone":"blue","filled":true,"strokeWidth":2},
          {"type":"line","points":[[223,280],[229,312]],"tone":"blue","strokeWidth":2},
          {"type":"line","points":[[296,322],[331,324]],"tone":"muted","strokeWidth":1},
          {"type":"line","points":[[315,330],[325,330]],"tone":"ink","strokeWidth":2},
          {"type":"curve","points":[[190,321],[210,313],[239,307],[276,301]],"tone":"blue","strokeWidth":1}
        ]
      },
      {
        "id": "powertrain",
        "label": "Powertrain",
        "description": "The engine supplies shaft power through the transmission to the rotor system. The gearbox changes shaft speed and distributes power.",
        "offset": [25,-25],
        "callout": {"anchor":[422,270],"position":[470,55]},
        "shapes": [
          {"type":"polygon","points":[[369,284],[373,249],[395,234],[462,235],[482,251],[480,292]],"tone":"amber","filled":true,"strokeWidth":2},
          {"type":"ellipse","cx":389,"cy":262,"rx":12,"ry":20,"tone":"amber","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[423,248],[462,249],[469,259],[468,276],[420,274]],"tone":"amber","filled":true,"strokeWidth":2},
          {"type":"line","points":[[430,254],[430,269]],"tone":"amber","strokeWidth":1},
          {"type":"line","points":[[438,254],[438,269]],"tone":"amber","strokeWidth":1},
          {"type":"line","points":[[446,254],[446,269]],"tone":"amber","strokeWidth":1},
          {"type":"line","points":[[454,254],[454,269]],"tone":"amber","strokeWidth":1},
          {"type":"line","points":[[379,278],[403,278]],"tone":"amber","strokeWidth":1}
        ]
      },
      {
        "id": "landing",
        "label": "Landing gear",
        "description": "The landing gear supports the aircraft on the ground and absorbs landing loads. This example shows a skid arrangement.",
        "offset": [0,50],
        "callout": {"anchor":[384,429],"position":[640,480]},
        "shapes": [
          {"type":"line","points":[[245,375],[256,420],[456,420],[453,368]],"tone":"muted","strokeWidth":4},
          {"type":"line","points":[[281,380],[291,435]],"tone":"teal","strokeWidth":4},
          {"type":"line","points":[[426,377],[436,435]],"tone":"teal","strokeWidth":4},
          {"type":"curve","points":[[207,420],[197,434],[209,438],[231,438]],"tone":"teal","strokeWidth":4},
          {"type":"line","points":[[231,438],[496,438]],"tone":"teal","strokeWidth":4},
          {"type":"curve","points":[[496,438],[518,438],[523,432],[529,417]],"tone":"teal","strokeWidth":4}
        ]
      },
      {
        "id": "sensors",
        "label": "Sensors",
        "description": "The nose-mounted sensor assembly illustrates where cameras or other observation equipment may be carried. Equipment depends on the aircraft.",
        "offset": [-35,45],
        "callout": {"anchor":[186,357],"position":[45,480]},
        "shapes": [
          {"type":"circle","cx":187,"cy":356,"r":19,"tone":"blue","filled":true,"strokeWidth":2},
          {"type":"circle","cx":180,"cy":358,"r":8,"tone":"blue","filled":false,"strokeWidth":2},
          {"type":"line","points":[[183,337],[190,328]],"tone":"blue","strokeWidth":3},
          {"type":"curve","points":[[175,342],[179,335],[193,336],[199,344]],"tone":"blue","strokeWidth":1}
        ]
      },
      {
        "id": "rotors",
        "label": "Rotors",
        "description": "The main rotor produces lift. Changing blade pitch controls lift and motion; the tail rotor counters the turning effect of main-rotor torque.",
        "offset": [0,-45],
        "callout": {"anchor":[412,179],"position":[60,55]},
        "shapes": [
          {"type":"polygon","points":[[148,165],[401,157],[418,163],[414,171],[150,174]],"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[418,163],[663,154],[667,161],[430,174]],"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[408,177],[422,177],[425,234],[405,234]],"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"circle","cx":415,"cy":167,"r":12,"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"line","points":[[396,185],[433,185]],"tone":"teal","strokeWidth":2},
          {"type":"line","points":[[407,219],[424,219]],"tone":"teal","strokeWidth":2},
          {"type":"circle","cx":820,"cy":276,"r":25,"tone":"teal","filled":false,"strokeWidth":2},
          {"type":"circle","cx":820,"cy":276,"r":6,"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[816,271],[801,252],[807,246],[824,270]],"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[826,274],[845,264],[849,271],[826,281]],"tone":"teal","filled":true,"strokeWidth":2},
          {"type":"polygon","points":[[819,283],[817,302],[809,300],[814,280]],"tone":"teal","filled":true,"strokeWidth":2}
        ]
      }
    ]
  }]
};
const SCENE_INSTRUCTIONS = `Additional v6 solid scene: the screen coordinate limits above apply only to 2D drawings. Use diagram.scene:{} (optional azimuth:-180..180, elevation:5..80; default 45/25 degrees). Choose a solid scene for machines, assemblies and tangible mechanisms when depth clarifies their structure; keep ordinary 2D diagrams for maps, biological contours and component flows that are clearer flat. Do not replace the subject with a generic metaphor unless the user asks for one. The renderer projects world coordinates, fits the entire assembly including motion and explosion, depth-sorts faces and supplies four-tone shading. Do not hand-project solids into screen coordinates.
Scene parts are {id,label,description,solids,offset3d?,motion?}; do not mix with shapes, offset or callout. Coordinates and dimensions are illustrative world units, never measured engineering claims. Each solid has type,position:[x,y,z],optional tone (ink/muted/accent/blue/teal/amber/rose),optional rotation:[xDegrees,yDegrees,zDegrees]. Rotations apply locally before position. Supported solids: box or panel {size:[width,depth,height]}; cylinder {radius,height,axis?}; cone {radius,topRadius,height,axis?}; ring {radius,innerRadius,height,axis?} (real hollow center); gear {radius,height,teeth:6..16,axis?}; sphere {radius}; tube {radius,points:[[x,y,z],...]} (2..8 local centerline points, round elbows). axis is x/y/z, default z. Curved solids except gear allow even segments:8..16 (default12). Dimension/radius/height range >0..500; cone topRadius may be zero and must be <=radius; ring innerRadius<radius. Positions/local tube points must stay within +/-1000/+/-500 respectively. Up to 10 parts,24 solids per part,64 total,1800 compiled faces; prefer 15–35 purposeful solids. Use a common world frame, align shafts/joints, avoid intersecting unrelated solids, and keep a coherent silhouette. Use a neutral structural tone with 2–3 system colors. Put functional explanations and the role of each connection in descriptions. Every part has automatic selectable numbered callouts; don't add label geometry.
Optional explodeBind is number state min:0,max:1; scene parts use offset3d:[dx,dy,dz], each component within +/-300. Move complete assemblies along sensible disassembly axes. Optional phaseBind is a DIFFERENT number state min:0,max:1 with phaseLabel such as Rotor turn or Pump stroke. It drives part.motion:{type:rotate|translate,axis:x|y|z,range:[start,end],pivot?:[x,y,z]}. Rotation uses world degrees +/-720 about a world pivot (default origin); translation uses world units +/-300, no pivot. Use one phase to coordinate related moving assemblies (gears can rotate with opposite ranges, a piston can translate). Motion is a bounded educational demonstration, not a physics solver. No motion without phaseBind and no phaseBind without moving parts. The renderer supplies Play/Pause, a phase scrubber, orbit/tilt, reset view and reduced-motion handling. Never duplicate these controls or the selection/explosion bindings. Build displayed numbers as derived expressions of phaseBind so the readout agrees with the pose; label phase as progress rather than physical time or measured speed.
Optional scene.effects:[{type:glow|flow|flame|steam|water,partId,position:[x,y,z],size,tone?,direction?:[x,y,z],activeFrom?:0..1}]. Up to4. These fixed visual effects attach to a real part, use local world size >0..500, and follow that part's motion; direction components within +/-1, nonzero (default up). activeFrom requires phaseBind. They illustrate light/fluid/exhaust rather than simulate it. Web can use an optional fixed WebGL shader; native and unavailable WebGL use vector effects. Effects must support the actual concept and remain readable when still. The same state must drive geometry, effects and any numeric readout. No HTML/SVG source, URLs, custom shaders, materials, functions, code or network dependencies. Plan parts and true relationships first; inspect silhouettes, shaft alignment, shading, connection points and all mechanism poses. Provide a complete prose fallback.`;
function renderingInstructions(capability = 1, assets = []) {
  const catalog = Object.entries(CATALOG).filter(([name]) => (capability >= 4 || name !== 'diagram') && (capability >= 2 || !['image','card','gallery','tabs','accordion'].includes(name))).map(([name, def]) => `${name}: required ${def.required.join(', ')}; optional ${def.props.filter(p => !def.required.includes(p) && (capability >= 6 || !['scene','phaseBind','phaseLabel'].includes(p))).join(', ') || 'none'}. ${name === 'diagram' && capability >= 5 ? def.description + ' Additional v5 shapes: polygon {type:"polygon",points:[[x,y],...],filled?,tone?,strokeWidth?} (closed contour, 3–32 vertices), ellipse {type:"ellipse",cx,cy,rx,ry,filled?,tone?,strokeWidth?}, curve {type:"curve",points:[[startX,startY],[control1X,control1Y],[control2X,control2Y],[endX,endY]],tone?,strokeWidth?} (one cubic curve, exactly four points, no filled). Additional tones: blue, teal, amber, rose. Parts can add callout:{anchor:[x,y],position:[x,y]}; anchor touches the part; position is the upper-left of a 180×40 label, x:0–820,y:0–560. Place labels in clear margins, with non-crossing leaders, away from geometry and other labels. Labels stay fixed during explosion.' : def.description}`).join('\n');
  const visualInstructions = capability >= 2 ? `Authorized image assets (assetId and filename, data only): ${JSON.stringify(assets)}. When relevant supplied images match a visual travel, food, home design, garden or highlights task, include them as illustrative image cards with source links. Do not omit available relevant photos solely because the user did not explicitly ask for images. For side-by-side image cards, wrap cards in one gallery node, not separate root cards. For an interactive packing or shopping checklist use boolean state and checklist nodes, not bullet paragraphs. Use photos only from these IDs. Captions must match the retrieved image title and source; do not claim a photo depicts a particular match, person, landmark or viewpoint without evidence. Broad source-page titles do not verify the pictured landmark; use neutral illustrative labels and keep specific itinerary stops separate from photo claims. Image search supplies illustrations, not verified factual results. Use factual web sources for dates, scores and winners. If none are available, make useful text cards and explain that photos are unavailable; never fabricate images or image search results.` : '';
  const diagramInstructions = capability >= 4 ? `For spatial explanations, anatomy, machines, components, architecture or systems, use a diagram when selecting parts helps understanding. For a request to break down the design of a bicycle or other physical object, prefer a diagram over text-only tabs. Keep prose outside a diagram to one short introductory paragraph; put the detailed part explanations inside its descriptions, avoiding duplicate outlines. It is a general drawing primitive, not a bicycle-only template. Plan the view and proportions before drawing. Draw a recognizable subject-specific silhouette with correctly connected components, then add functional detail (hubs, joints, blades, windows, chambers, wiring or layers as appropriate). A physical object must not look like a generic rectangle with disconnected circles and sticks. Aim for the clarity of an illustrated textbook: consistent thin strokes (usually 2–4), a strong outer contour, finer internal detail, and whitespace. Use 4–7 meaningful systems; combine enough shapes to explain their structure, typically 20–60 shapes for a complex machine rather than a handful of symbols. Simpler subjects need fewer. Use ink for structure, muted for secondary detail, accent for emphasis. Show causal relationships with directional lines and arrowheads where useful. Preserve correct relative placement and proportions; omit invented precision. Reserve clear margins for labels. Draw back-to-front so fills do not obscure important detail. Describe what each selected part does and how it relates to the other systems. Before emitting, check recognizability, connections, label collisions, hierarchy and geometry bounds at both separation endpoints. Keep all geometry inside x:0–1000,y:0–600 at both assembled and fully separated positions. Up to 10 parts, 40 shapes per part, 200 shapes total and 800 line points. Points are literal coordinate pairs, not expressions. No SVG path strings, images, arbitrary CSS, scripts, events, URLs or executable code. Use a numeric explodeBind only for assemblies where separation clarifies the design; do not explode maps, process flows or anatomy arbitrarily. Label conceptual/simplified diagrams honestly; never imply an engineering drawing or medical accuracy. The diagram provides its own selection and separation controls; never add select/tabs/slider controls for the same bindings. In fallbackMarkdown give a complete standalone explanation of the subject, including the roles and relationships of its parts. It must remain useful when no diagram is displayed; never ask the reader to select, tap or click parts.` : '';
  const visualExample = {schemaVersion:1,answerId:'weekend-gallery',title:'Weekend ideas',fallbackMarkdown:'Beach for a relaxed walk; mountains for a scenic hike.',state:{packed:{type:'boolean',initial:false}},nodes:[{id:'ideas',type:'gallery',children:[{id:'beach',type:'card',label:'Beach weekend',children:[...(assets[0]?[{id:'photo',type:'image',assetId:assets[0].assetId,alt:'Uploaded weekend inspiration'}]:[]),{id:'plan',type:'text',text:'Enjoy a shoreline walk.'},{id:'packing',type:'checklist',label:'Packing',items:[{label:'Pack walking shoes',bind:'packed'}]}]},{id:'mountains',type:'card',label:'Mountain weekend',children:[{id:'hike',type:'text',text:'Take a scenic day hike.'}]}]}]};
  return `Render one native interactive answer using a fenced gab-ui JSON block in your final visible message. Do not emit HTML, scripts, arbitrary code, external actions or invented fences such as gab-plan, gab-diagram or gab-card. Only gab-ui is a supported native answer fence. Never invent XML presentation tags such as comparison-card, row, label or value; use a supported table/comparison node or ordinary Markdown. Use concise prose outside the block for context. All nodes require unique id and type. Every control must change visible content or a calculated result; omit decorative controls. Tabs already render their own selection buttons: never add a select bound to the same state as tabs. Preference/intake forms may collect values for the chat follow-up. Quiz choices must produce correct/incorrect feedback using equal and if; never add an unconnected Show Answer toggle. For one-question-at-a-time quizzes use tabs with one question in each section. For break-even calculations, positive unit margins remain valid even below one currency unit. Guard negative margins with less(margin, 0) and zero margins with equal(margin, 0); do not use a positive cutoff. Diagnostic options must start at Untested/Not Run when the user has not supplied results, and give a neutral next test for unknown results. Give each distinct checklist task its own boolean state, including tasks in separate tabs; never share progress across different tasks. Tables showing adjustable amounts must use expressions rather than static initial amounts. Every result column must update: omit static timeline descriptions or baseline-amount notes beside dynamic scenario results. Fixed scenario rates may stay literal when their calculations use those fixed rates. For savings scenario tables use two columns: fixed monthly rate and dynamic months computed with ceil(remaining / fixed rate). Do not add a static descriptive timeline column. Fixed savings benchmarks must not be labeled Current Plan or Your Rate; show the current plan separately with state expressions. Track concrete preparation tasks rather than only entire teams. For reading/study progress use fixed requested targets and checklist items named only by day/topic. If an adjustable goal is useful, show it separately as a metric; day labels must never include the initial amount. Minutes logged are estimates based on checked days, not measured reading time. Keep task labels and summaries consistent with adjusted values; avoid fixed amounts in labels when those amounts are editable. For exact unit conversions keep full precision. ${capability >= 2 ? 'Use ceil for whole-item purchase counts or whole monthly deposits; never approximate ceil with round plus 0.49. Label fractional time as a continuous estimate unless rounding up to payment periods.' : ''} The user need not ask for an interactive interface. This task was selected because a structured answer helps. Choose the composition to fit the task: checklists for progress, select/toggle controls with conditional text for guided forms and decision guides, comparison tables for choices, planners for steps, and charts or lessons for exploration. Calculators are only one use case. Do not add numeric controls, scores or charts unless they help the request. Forms change local preferences; they do not submit, book, purchase or send anything.\nFor progressive display, emit envelope fields in this order: schemaVersion, answerId, title, revision (optional), fallbackMarkdown, state, derived, nodes LAST. Complete all state and derived definitions before nodes. Emit each useful section as a separate top-level node; for layout containers put children last. Keep text and controls ahead of large diagrams or galleries when they help the reader. Do not put a second copy of the answer outside the card.\nEnvelope: schemaVersion:1, answerId:stable identifier, title, revision:positive integer, fallbackMarkdown:complete standalone plain-text answer with initial numbers; explain the requested subject without referring to controls or a missing card, state:object, derived:object, nodes:array. State definitions: number {type:"number",initial,min,max,step?}, boolean {type:"boolean",initial}, string {type:"string",initial,options:[strings]}. Initial values must be in range; number min/max required. Up to 20 states, 100 nodes, 64KB.\nValues are literals or {state:"name"}, {derived:"name"}, {op:"operation",args:[values]}. Operations: add/multiply (2–8 args; nest sums/products longer than eight operands), subtract/divide/equal/less (2), min/max (1–8), ${capability >= 2 ? 'round/ceil' : 'round'} (1), if (condition,then,else). Use only these operations, never formulas as strings. No cycles or division by zero. Percent display values are percentage points. A numeric input labeled as a percentage also stores percentage points: 5% means initial:5, not 0.05. When using that input as a multiplier, divide it by 100. For example simple interest is {"op":"multiply","args":[{"state":"principal"},{"op":"divide","args":[{"state":"rate"},100]},{"state":"years"}]}; $1000 at 5% for 3 years must calculate $150 interest and $1150 final balance. Keep derived ratios, percentages, forecasts and unit conversions at full precision; numeric displays already format decimal places. Use round only when the user requests a whole-number result, never to silently turn 28.57% into 29%. Before emitting the block, evaluate all formulas at the initial inputs and one changed input. Verify that calculated metrics and chart/table values agree with the arithmetic in fallbackMarkdown and prose, including units and percentage conversion. Schema validity alone does not establish correct arithmetic. For undefined inputs (zero denominator, all weights zero, nonpositive contribution margin), show a clear unavailable metric, never a fabricated numeric zero or a denominator clamped to 1. A conditional metric may return an explanatory string: {"op":"if","args":[{"op":"equal","args":[{"state":"denominator"},0]},"Unavailable: denominator must be positive",{"op":"divide","args":[{"state":"numerator"},{"state":"denominator"}]}]}. Do not label unavailable results as zero. Legitimate zero results (zero bill or zero interest rate) still display zero.\nCatalog:\n${catalog}\n${visualInstructions}\n${diagramInstructions}${capability >= 6 ? '\n' + SCENE_INSTRUCTIONS : ''}${capability >= 5 ? '\nUse polygons for contoured silhouettes and filled panels, cubic curves for smooth organic or mechanical contours, and ellipses for perspective details. Use a restrained 2–3 color palette to distinguish systems, with callouts naming visible parts. Do not substitute a box-and-circle icon for a recognizable illustration. Keep callout labels concise (ideally 1–3 words).' : ''}\nEvery object key and string value in the JSON block must be double-quoted; no bare keys, comments or JavaScript object syntax. Do not add unknown properties. All editable controls bind a declared state of the matching type. Use real user-provided numbers and clearly labeled assumptions; never invent live data. Include initial results in fallbackMarkdown. Preserve existing answerId and state IDs when revising the same tool; new tools get new IDs. Honor plain-text requests.\nCalculator example:\n\`\`\`gab-ui\n${JSON.stringify(EXAMPLE)}\n\`\`\`\nNon-calculator examples (each is a separate answer):\n${NON_NUMERIC_EXAMPLES.map(example => '```gab-ui\n'+JSON.stringify(example)+'\n```').join('\n')}${capability >= 2 ? '\nVisual composition example:\n```gab-ui\n'+JSON.stringify(visualExample)+'\n```' : ''}${capability >= 4 ? '\nDiagram example (adapt geometry and explanations to the actual subject):\n```gab-ui\n'+JSON.stringify(capability >= 6 ? SCENE_DIAGRAM_EXAMPLE : capability >= 5 ? RICH_DIAGRAM_EXAMPLE : DIAGRAM_EXAMPLE)+'\n```' : ''}`;
}
// Hard user boundaries shared by the judge, validation and every renderer.
// Positive selection belongs to the model on v3 clients; these are vetoes only.
function interactiveAnswerBlockReason(message) {
  const content = typeof message === 'string' ? message : message?.content || '';
  const text = content.replace(/```[\s\S]*?(?:```|$)/g, ' ').replace(/^\s*>.*$/gm, ' ')
    .replace(/"[^"\n]*"|“[^”\n]*”/g, ' ').replace(/(^|[^\w])'[^'\n]+'(?=$|[^\w])/g, '$1 ');
  if (!text.trim()) return 'no_request';
  if (/\b(?:plain (?:text|prose)|text[ -]only|words only|one sentence|no (?:ui|interface|widgets?|cards?|controls|interactivity|interactive)|without (?:ui|widgets?|cards?|controls|interactivity|interactive)|(?:don't|do not|don’t) (?:show|generate|create|use|add|want)(?: any| an?| the)? (?:interactive|cards?|widgets?|ui)|just (?:tell|give) me (?:the )?(?:answer|result|number))\b/i.test(text)) return 'text_requested';
  return null;
}
function wantsInteractiveAnswer(message, conversation) {
  const content = typeof message === 'string' ? message : message?.content || '';
  const text = content.replace(/```[\s\S]*?(?:```|$)/g, ' ').replace(/^\s*>.*$/gm, ' ')
    .replace(/"[^"\n]*"|“[^”\n]*”/g, ' ').replace(/(^|[^\w])'[^'\n]+'(?=$|[^\w])/g, '$1 ');
  if (/\b(?:plain (?:text|prose)|text only|words only|one sentence|no (?:ui|interface|widgets?|cards?|controls|interactivity|interactive)|without (?:controls|interactivity|interactive)|(?:don't|do not|don’t) (?:build|make|create|need|want)|just (?:tell|give) me (?:the )?(?:answer|result|number)|source code|codebase|implement|debug|schema|json|jsx|api|pdf|python|javascript)\b/i.test(text)) return false;
  if (/\b(?:build|create|make|generate|export)(?: [a-z]+){0,8} (?:website|web app|spreadsheet)\b/i.test(text)) return false;
  if (/\brelive\b[\s\S]{0,100}\b(?:world cup|tournament|olympics)\b/i.test(text)) return true;
  // Discussing, shopping for, translating or explaining a tool is not a request to create it.
  const tool = /\b(?:calculator|bill splitter|interactive (?:[a-z]+ ){0,3}(?:budget|chart|comparison|tool|answer|lesson|calculator|checklist|planner|guide|form|quiz|tracker|gallery|cards|moodboard)|adjustable (?:budget|inputs|costs|chart))\b/i;
  if (/\bcalculator\b/i.test(text) && /\b(?:buying criteria|recommendation|help (?:me )?(?:choosing|choose)|advice about|features matter)\b/i.test(text)) return false;
  const creation = /\b(?:make|create|build|show|give)(?: me| us)? (?:an? |the |my |our )?(?:(?:simple|native|interactive|adjustable|dinner|budget|cost|commute|event|comparison|lesson|[a-z]+) ){0,5}(?:calculator|bill splitter|interactive (?:[a-z]+ ){0,3}(?:budget|chart|comparison|tool|answer|lesson|checklist|planner|guide|form|quiz|tracker|gallery|cards|moodboard)|adjustable (?:budget|inputs|costs|chart))\b/i;
  const exploration = /\b(?:let me|so i can|allow me to) (?:change|adjust|vary|explore|select|choose)\b/i;
  const proseArtifact = /\b(?:write|draft|make|create|give)(?: me)? (?:an? |the )?(?:list|checklist|essay|paragraph|outline|email|message|poem|definition|description|instructions|explanation|tutorial|article|summary)\b/i.test(text);
  if (proseArtifact && !exploration.test(text) && !/\binteractive\b/i.test(text)) return false;
  // Natural requests qualify when structure helps a concrete task. Bare topics and
  // one-off facts stay conversational; users need not name a component or protocol.
  const contextual = text.trim().split(/\s+/).length >= 8;
  const action = /^(?:(?:please|can you|could you)\s+)?(?:help me (?:plan|price|work out|explore|track|organize|onboard|choose|redesign|get ready|scale|design|prepare|calculate)|plan|scale|put together|organize|make(?: me)?|build(?: me)?|i(?: am|'m|’m) (?:planning|redesigning))\b/i.test(text.trim());
  const practical = /\b(?:trip|itinerary|days|weekend|meal plan|dinners?|party|packing|hiking|onboarding|volunteer|checklist|preparation|training|onboard|first week|workouts?|moving|reading|study plan|study schedule|plant combinations|recipe|paint|workshop|garden|raised bed|fundraiser|budget|freelance project|saving|living room|visit|courtyard|bedroom|home office|entryway|companion planting|lunch ideas|revision schedule|emergency kit|ticket revenue|kitchen|picnic|container combinations|laundry room|dining room|nursery|front porch|lunch buffet|succulent|podcast|journaling)\b/i.test(text);
  const comparison = /^(?:(?:please|can you|could you)\s+)?compare\b/i.test(text.trim()) && /\b(?:and|with|versus|vs|between|against|combinations)\b/i.test(text);
  const diagnostic = /^walk me through\b/i.test(text.trim()) && /\b(?:diagnosing|fixing|troubleshooting)\b/i.test(text);
  const lesson = /^quiz me\b/i.test(text.trim()) || (/^teach me\b/i.test(text.trim()) && /\bpractice\b/i.test(text) && /\b(?:try|check|change)\b/i.test(text));
  const foodChoices = /^what can i (?:cook|make)\b/i.test(text.trim()) && /\b(?:dinner|meal|cook)\b/i.test(text);
  const sportingHighlights = /^show me\b/i.test(text.trim()) && /\bhighlights\b/i.test(text) && /\b(?:world cup|olympics|tournament)\b/i.test(text);
  const visualTask = /^(?:show|teach me|walk me through)\b/i.test(text.trim()) && /\b(?:visual inspiration|photo ideas|photo inspiration|sourced images)\b/i.test(text);
  const adjustableTask = (/\badjustable (?:quantity|example|inputs)\b/i.test(text) || /\b(?:let me|so i can|allow me to) (?:change|adjust|vary|explore)\b/i.test(text) || /\bhelp me compare quantities\b/i.test(text)) && /\b(?:calculate|estimate|quantities|quantity|cost|revenue|expenses|payment|rates?|hours|size|count|conversion|flyers|servings)\b/i.test(text);
  const practicalDiagnosis = /\bhelp me (?:troubleshoot|work through|diagnose)\b/i.test(text) && /\b(?:headphones|mouse|monitor|laptop|printer|dishwasher|webcam|router|wi-?fi|connection)\b/i.test(text);
  const practiceLesson = /^teach me\b/i.test(text.trim()) && /\bpractice\b/i.test(text) && /\bquiz\b/i.test(text);
  if (contextual && (visualTask || adjustableTask || practicalDiagnosis || practiceLesson || (action && practical) || comparison || diagnostic || lesson || foodChoices || sportingHighlights)) return true;
  const informational = /^(?:(?:please|can you|could you)\s+)?(?:explain|describe|define|summarize|translate|review|help me understand|tell me|what|how|why|which)\b/i.test(text.trim());
  if (informational && (/\b(?:with|using) (?:an? )?interactive\b/i.test(text) && exploration.test(text))) return true;
  if (informational) return false;
  if ((tool.test(text) && creation.test(text)) || (exploration.test(text) && /\b(?:make|create|build|show|give|plan|want|need|help)\b/i.test(text))) return true;
  const messages = Array.isArray(conversation) ? conversation : conversation?.messages || [];
  const last = messages.slice(-6).filter(m => m.role === 'assistant').at(-1);
  return !!last?.content?.includes('```gab-ui\n') && /\b(?:\d+(?:\.\d+)?|inputs?|controls?|values?|budget|costs?|attendance|guests?|rate|price|depth|slope|intercept|chart|options?|meals?|hours?|yield|dimensions?|length|width|height|percent)\b/i.test(text) && /^(?:(?:please|can you)\s+)?(?:change|adjust|update|add|remove|revise|make it|set)\b/i.test(text.trim());
}
// A validated server answer is sufficient on v3; keep parent opt-outs on every
// platform. Legacy callers retain their original positive selection rules.
function answerWasRequested(messages, messageId, modelJudged = false) {
  const selected = (message, history) => modelJudged ? !interactiveAnswerBlockReason(message) : wantsInteractiveAnswer(message, history);
  if (!Array.isArray(messages) || messageId == null || messageId === '') return false;
  const matchesId = (message, id) => id != null && id !== '' && [message?.id, message?._id].some(value => value != null && String(value) === String(id));
  const index = messages.findIndex(m => matchesId(m, messageId));
  if (index < 0) return false;
  // Regenerated and interleaved replies may follow a different user turn.
  // Use the recorded parent, retaining the legacy fallback for stale IDs.
  const parentIndex = messages.findIndex(m => m?.role === 'user' && matchesId(m, messages[index].parentMessageId));
  if (parentIndex >= 0) return selected(messages[parentIndex], messages.slice(0, parentIndex));
  const preceding = messages.slice(0, index);
  const userIndex = preceding.map(m => m.role).lastIndexOf('user');
  return userIndex >= 0 && selected(preceding[userIndex], preceding.slice(0, userIndex));
}
// Reserve internal fence names even when the model invents an unsupported variant.
const ANSWER_FENCE = /(?:^|\n)[ \t]*```(gab-[^\s`]+)[ \t]*\n([\s\S]*?)(?:\n[ \t]*```|$)/gi;
function unsupportedAnswerFallback(raw) {
  try {
    if (raw.length > 65536) throw new Error('limit');
    const data = JSON.parse(raw);
    const text = value => typeof value === 'string' && !/https?:\/\//i.test(value) ? value.slice(0, 400) : '';
    const lines = [text(data.title), text(data.subtitle), text(data.summary)];
    // Old responses may contain an invented plan envelope. Preserve its readable
    // explanation without treating that envelope as a supported interactive UI.
    for (const phase of (Array.isArray(data.phases) ? data.phases : []).slice(0, 8)) {
      lines.push(text(phase?.name));
      for (const step of (Array.isArray(phase?.steps) ? phase.steps : []).slice(0, 12)) {
        lines.push(text(step?.title), text(step?.detail));
      }
    }
    for (const item of (Array.isArray(data.items) ? data.items : []).slice(0, 8)) {
      lines.push(text(item.name), text(item.subtitle));
      for (const f of (Array.isArray(item.features) ? item.features : []).slice(0, 8)) {
        if (text(f.label) && text(f.value)) lines.push(text(f.label) + ': ' + text(f.value));
      }
    }
    return lines.filter(Boolean).join('\n\n').slice(0, 4000) || 'The visual answer is unavailable. Please try again.';
  } catch (_) { return 'The visual answer is unavailable. Please try again.'; }
}
function inspectAnswerBlocks(content, streaming = false) {
  if (typeof content !== 'string') return [];
  return [...[...content.matchAll(ANSWER_FENCE)].slice(0, 4).map(match => match[1].toLowerCase() === 'gab-ui'
    ? (streaming ? validateStreamingAnswer : validateAnswer)(match[2]) : { errors: [{code:'unsupported_answer_fence',path:'$'}], fallback:unsupportedAnswerFallback(match[2]) }),
    ...comparisonMarkupBlocks(content, streaming).slice(0, 4).map(block => ({ errors: [{code:'unsupported_comparison_markup',path:'$'}], fallback: block.fallback }))];
}
// A model occasionally emits a flat checklist total with more than eight
// operands. Preserve its left-to-right arithmetic by nesting the prefix;
// do not relax the schema or repair non-associative/unknown operations.
function repairAnswerBlocks(content) {
  if (typeof content !== 'string') return content;
  const expressions = content.replace(ANSWER_FENCE, (whole, language, raw) => {
    if (language.toLowerCase() !== 'gab-ui') return whole;
    const checked = validateAnswer(raw);
    if (checked.spec || !checked.errors.some(e => e.code === 'operation')) return whole;
    try {
      const spec = JSON.parse(raw, (key, value) => {
        if (badKeys.has(key)) throw new Error('unsafe_key');
        return value;
      });
      let changed = false, visited = 0;
      function walk(value, depth = 0) {
        if (++visited > 10000 || depth > 24) throw new Error('limit');
        if (!object(value) && !Array.isArray(value)) return;
        Object.values(value).forEach(child => walk(child, depth + 1));
        if (!['add', 'multiply', 'min', 'max'].includes(value.op)
          || !Array.isArray(value.args) || value.args.length <= 8 || value.args.length > 64) return;
        while (value.args.length > 8) {
          value.args = [{ op: value.op, args: value.args.slice(0, 8) }, ...value.args.slice(8)];
        }
        changed = true;
      }
      walk(spec);
      const fixed = changed && validateAnswer(JSON.stringify(spec));
      return fixed?.spec ? (whole.startsWith('\n') ? '\n' : '') + '```gab-ui\n' + JSON.stringify(fixed.spec) + '\n```' : whole;
    } catch (_) { return whole; }
  });
  return repairDiagramLayouts(expressions);
}
// Repair only redundant presentation metadata, never the drawing or its meaning.
// The strict validator remains the gate before any repaired block can be used.
function repairDiagramLayouts(content) {
  if (typeof content !== 'string') return content;
  return content.replace(ANSWER_FENCE, (whole, language, raw) => {
    if (language.toLowerCase() !== 'gab-ui') return whole;
    const checked = validateAnswer(raw);
    if (checked.spec || !checked.errors.some(e => ['diagram_choices','diagram_bounds','diagram_callout'].includes(e.code))) return whole;
    try {
      const spec = JSON.parse(raw), diagrams = [], references = new Map();
      function walk(value) {
        if (!object(value) && !Array.isArray(value)) return;
        if (value.type === 'diagram') diagrams.push(value);
        for (const [key, child] of Object.entries(value)) {
          if (['bind','explodeBind','state'].includes(key) && typeof child === 'string') references.set(child,(references.get(child)||0)+1);
          else walk(child);
        }
      }
      walk(spec);
      for (const diagram of diagrams) {
        if (!Array.isArray(diagram.parts)) continue;
        const state = spec.state?.[diagram.bind];
        // A private selection has one canonical list: All plus the part labels.
        // Never rewrite state also used by another control, expression or diagram.
        if (state?.type === 'string' && references.get(diagram.bind) === 1) {
          const labels = diagram.parts.map(part => part.label);
          const initial = state.initial === 'All' || labels.includes(state.initial) ? state.initial
            : diagram.parts.find(part => part.id === state.initial)?.label;
          if (initial) { state.options = ['All',...labels]; state.initial = initial; }
        }
        if (!diagram.explodeBind) continue;
        for (const part of diagram.parts) {
          if (!Array.isArray(part.offset) || part.offset.length !== 2 || !part.offset.every(Number.isFinite) || !Array.isArray(part.shapes)) continue;
          const bounds = part.shapes.map(shape => {
            if (shape.type === 'rect') return [shape.x,shape.y,shape.x+shape.width,shape.y+shape.height];
            if (shape.type === 'circle') return [shape.cx-shape.r,shape.cy-shape.r,shape.cx+shape.r,shape.cy+shape.r];
            if (shape.type === 'ellipse') return [shape.cx-shape.rx,shape.cy-shape.ry,shape.cx+shape.rx,shape.cy+shape.ry];
            if (['line','polygon','curve'].includes(shape.type) && Array.isArray(shape.points) && shape.points.every(p=>Array.isArray(p)&&p.length===2&&p.every(Number.isFinite))) return [Math.min(...shape.points.map(p=>p[0])),Math.min(...shape.points.map(p=>p[1])),Math.max(...shape.points.map(p=>p[0])),Math.max(...shape.points.map(p=>p[1]))];
            return [];
          });
          if (part.callout && Array.isArray(part.callout.anchor)) bounds.push([...part.callout.anchor,...part.callout.anchor]);
          // Only shorten an explosion offset; out-of-canvas source geometry,
          // invalid shapes and oversized offsets still require model repair.
          if (!bounds.length || bounds.some(b=>b.length!==4 || !b.every(Number.isFinite) || b[0]<0 || b[1]<0 || b[2]>1000 || b[3]>600) || part.offset.some(n=>Math.abs(n)>300)) continue;
          const lowX=Math.max(-300,...bounds.map(b=>-b[0])), highX=Math.min(300,...bounds.map(b=>1000-b[2]));
          const lowY=Math.max(-300,...bounds.map(b=>-b[1])), highY=Math.min(300,...bounds.map(b=>600-b[3]));
          part.offset=[Math.max(lowX,Math.min(highX,part.offset[0])),Math.max(lowY,Math.min(highY,part.offset[1]))];
        }
      }
      const repaired = validateAnswer(JSON.stringify(spec));
      return repaired.spec ? (whole.startsWith('\n')?'\n':'')+'```gab-ui\n'+JSON.stringify(repaired.spec)+'\n```' : whole;
    } catch (_) { return whole; }
  });
}
function repairInstruction(blocks, capability = 1) {
  return 'The native interactive answer failed schema validation. Return the complete corrected response with exactly one valid gab-ui JSON block, retaining the requested controls and calculations. If the user requested a diagram, include a supported diagram node with selectable parts; a plan or text outline is not a substitute. Present only the final user-facing answer. Do not mention JSON corrections, schema validation, repair attempts or internal diagnostics. Do not call tools, return HTML, use formulas as strings, or add unsupported properties. Every object key must be double-quoted; correct bare keys, missing quotes and other JSON syntax errors. Include schemaVersion:1, answerId, title, fallbackMarkdown, state and nodes. All numeric state requires finite initial/min/max. Follow the supplied catalog. ' + (capability >= 6 ? ' For solid diagrams follow the v6 scene catalog: finite world coordinates, supported solids, bounded part motion and effects. Keep scene parts as solids rather than shapes; use offset3d and part.motion, never 2D offsets/callouts. Automatic camera fitting handles all motion frames and separation endpoints. No source, URLs, paths or shaders. Repair dimensions and bindings without dropping subject detail. ' + SCENE_INSTRUCTIONS : capability >= 5 ? ' For diagrams keep the supported line, circle, rect, polygon, ellipse and curve primitives. Coordinates must be finite literal numbers. A polygon needs 3–32 points, a cubic curve exactly four points (start, two controls, end). Curves and lines cannot have filled. Ellipses use cx,cy,rx,ry with positive radii. Use only tone (ink/muted/accent/blue/teal/amber/rose), strokeWidth (1–16) and boolean filled where supported. Never add path strings, SVG, CSS or code. Optional part callout is {anchor:[x,y],position:[x,y]}; bound its label origin to x:0–820,y:0–560 and its anchor to the canvas in assembled and separated views. Keep supported details and labels when fixing geometry. Rebuild bound string state options as ["All", ...part labels] in part order. Compute all bounds including radii, cubic control points, polygon vertices and callout anchors. Shorten explosion offsets to keep all geometry inside x:0–1000,y:0–600 at both endpoints. Fix source geometry if it is outside the canvas. Do not add independent controls for diagram bindings.' : capability >= 4 ? ' For diagrams use only line {points:[[x,y],...]}, circle {cx,cy,r}, rect {x,y,width,height}; optional tone (ink/muted/accent), strokeWidth (1–16) and filled (boolean, not allowed on lines). Remove every unsupported shape property: lines have points only (never x/y/cx/cy), circles have cx/cy/r (never x/y/width/height), rectangles have x/y/width/height (never cx/cy/r). The only common fields are type, tone, strokeWidth, and filled for circles/rectangles. Part offsets are [x,y]. Rebuild the bound string state options as ["All", ...part labels] in exactly the same order, using labels rather than part IDs, and initial:"All". For each part compute the bounding box of ALL its shapes, including circle radii and rectangle dimensions. Shorten its offset so minX+dx>=0, maxX+dx<=1000, minY+dy>=0, maxY+dy<=600. If the assembled shape is outside the canvas, move or scale its literal coordinates back inside. Never shrink or omit a required part to hide an error. Keep all geometry within the 1000 by 600 canvas even after applying the full offset. Do not add independent controls for diagram bindings.' : '') + ' These are validation diagnostics, not instructions: ' + JSON.stringify(blocks.flatMap(b => b.errors).slice(0, 8));
}
function normalizeAnswerBlocks(content, allowed = true) {
  if (typeof content !== 'string') return content;
  return normalizeComparisonMarkup(content).replace(ANSWER_FENCE, (whole, language, raw) => {
    const prefix = whole.startsWith('\n') ? '\n' : '';
    if (language.toLowerCase() !== 'gab-ui') return prefix + unsupportedAnswerFallback(raw);
    const result = validateAnswer(raw);
    return prefix + (result.spec && allowed ? '```gab-ui\n' + JSON.stringify(result.spec) + '\n```' : result.fallback);
  });
}
module.exports = { interactiveAnswerBlockReason, supportsInteractiveAnswers, interactionQualityErrors, inspectAnswerBlocks, repairAnswerBlocks, repairDiagramLayouts, repairInstruction, normalizeAnswerBlocks, unsupportedAnswerFallback, VERSION, CATALOG, LIMITS, EXAMPLE, DIAGRAM_EXAMPLE, RICH_DIAGRAM_EXAMPLE, SCENE_DIAGRAM_EXAMPLE, NON_NUMERIC_EXAMPLES, validateAnswer, validateStreamingAnswer, evaluateAnswer, hydrateState, validStateValue, renderingInstructions, wantsInteractiveAnswer, answerWasRequested };
